import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from '@app/env';
import { catchError, Observable, of, tap } from 'rxjs';

interface MediaResponseModel {
  key: string;
}
@Injectable({ providedIn: 'root' })
export class MediaApiService {
  readonly #http = inject(HttpClient);

  uploadMediaFile(mediaFile: File): Observable<MediaResponseModel> {
    const formData = new FormData();
    formData.append('file', mediaFile);
    formData.append('accept-charset', 'UTF-8');

    return !mediaFile
      ? of({ key: '' })
      : this.#http
          .post<MediaResponseModel>(environment.mediaUrl, formData, {
            withCredentials: true,
            headers: new HttpHeaders().set('X-BOEMM-document-type', 'test'),
          })
          .pipe(catchError((error: HttpErrorResponse) => of({ key: '' })));
  }

  removeMediaFile(mediaFileKey: string): Observable<void> {
    return this.#http.delete<void>(
      `${environment.mediaUrl}/${mediaFileKey}`
    );
  }

  uploadPrivateMediaFile(mediaFile: File): Observable<MediaResponseModel> {
    const formData = new FormData();
    formData.append('file', mediaFile);
    formData.append('accept-charset', 'UTF-8');

    return this.#http.post<MediaResponseModel>(environment.privateMediaUrl, formData, {
      withCredentials: true,
      headers: new HttpHeaders().set('X-BOEMM-document-type', 'test'),
    });
  }

  removePrivateMediaFile(mediaFileKey: string): Observable<void> {
    return this.#http
      .delete<void>(`${environment.privateMediaUrl}/${mediaFileKey}`)
      .pipe(catchError(() => of(void 0)));
  }

  downloadContract(contractId: string, employeeId: string, fileName: string): Observable<Blob> {
    return this.#http
      .post(
        `${environment.apiBaseUrl}/employees/${employeeId}/documents/contracts/${contractId}/download`,
        {},
        { responseType: 'blob' },
      )
      .pipe(tap(blob => this.#saveFile(blob, fileName)));
  }

  downloadPaycheck(paycheckId: string, employeeId: string, fileName: string): Observable<Blob> {
    return this.#http
      .post(
        `${environment.apiBaseUrl}/employees/${employeeId}/documents/payslips/${paycheckId}/download`,
        {},
        { responseType: 'blob' },
      )
      .pipe(tap(blob => this.#saveFile(blob, fileName)));
  }

  #saveFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
